import React from "react";
import { Card, Progress, Tag } from "antd";
import {
  CheckCircle,
  Clock,
  Hourglass,
  LoaderCircle,
  ImageIcon,
  Lightbulb,
  Rocket,
} from "lucide-react";

export default function OptimizeProgress({
  progress = 0,
  imageUrl,
  categoryName,
  credits = 15,
}) {
  const steps = [
    {
      no: 1,
      title: "Analyzing Product Image",
      percent: progress >= 25 ? 100 : progress * 4,
      completed: progress >= 25,
      active: progress < 25,
    },
    {
      no: 2,
      title: "Creating Multiple Variations",
      percent: progress >= 50 ? 100 : progress > 25 ? (progress - 25) * 4 : 0,
      completed: progress >= 50,
      active: progress >= 25 && progress < 50,
    },
    {
      no: 3,
      title: "Finding Lowest Shipping Charge",
      percent: progress >= 75 ? 100 : progress > 50 ? (progress - 50) * 4 : 0,
      completed: progress >= 75,
      active: progress >= 50 && progress < 75,
    },
    {
      no: 4,
      title: "Preparing Optimized Image",
      percent:
        progress >= 100
          ? 100
          : progress > 75
          ? Math.min((progress - 75) * 4, 95)
          : 0,
      completed: progress >= 100,
      active: progress >= 75 && progress < 100,
    },
  ];

  return (
    <div className="w-full max-w-[1200px] mx-auto space-y-5">
      <div className="bg-gradient-to-r from-purple-50 via-white to-purple-50 border border-purple-100 rounded-2xl p-6">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    
    <div className="flex items-center gap-5">
      <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
          <Rocket
    size={45}
    className="text-purple-600"
  />
      </div>

      <div>
        <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          Optimizing Your Image
          ✨
        </h2>

        <p className="text-slate-600 mt-1">
          Please wait while we find the lowest shipping charge for your product image.
        </p>
      </div>
    </div>

    <div className="bg-purple-50 px-4 py-3 rounded-xl text-purple-700 font-semibold flex items-center gap-2">
      <Clock size={18} />
      This may take 2-3 minutes
    </div>

  </div>
</div>

      <div className="grid grid-cols-1 xl:grid-cols-[200px_minmax(0,1fr)_280px] gap-5">
        <Card className="rounded-2xl shadow-sm">
          <h3 className="font-bold text-center mb-4">Your Product Image</h3>

          <div className="h-[230px] lg:h-[130px] rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Product"
                className="w-full h-full object-contain"
              />
            ) : (
              <ImageIcon className="text-slate-400" size={42} />
            )}
          </div>

          <div className="mt-4 bg-purple-50 border border-purple-100 rounded-xl p-3">
            <p className="text-xs text-purple-600 font-semibold m-0">
              CATEGORY
            </p>
            <p className="font-bold text-purple-900 m-0">{categoryName}</p>
          </div>

          <div className="mt-3 bg-green-50 border border-green-100 rounded-xl p-3">
            <p className="text-xs text-green-600 font-semibold m-0">
              CREDITS REQUIRED
            </p>
            <p className="font-bold text-green-900 m-0">🪙 {credits} Credits</p>
          </div>
        </Card>

        <Card className="rounded-2xl shadow-sm">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={step.no}
                className="grid grid-cols-[48px_1fr_70px] gap-4"
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step.completed
                        ? "bg-green-500 text-white"
                        : step.active
                        ? "bg-purple-600 text-white"
                        : "bg-purple-100 text-purple-400"
                    }`}
                  >
                    {step.completed ? <CheckCircle size={22} /> : step.no}
                  </div>

                  {index !== steps.length - 1 && (
                    <div className="w-[2px] h-16 bg-purple-100 mt-2" />
                  )}
                </div>

                <div>
                  <h4 className="font-bold text-md m-0">{step.title}</h4>

                  <Progress
                    percent={Math.round(step.percent)}
                    showInfo={false}
                    strokeColor={step.completed ? "#22c55e" : "#9333ea"}
                    trailColor="#eee7ff"
                    className="mt-5"
                  />
                </div>

                <div className="flex flex-col items-end gap-3 pt-1">
                  <span className="font-bold text-purple-700">
                    {Math.round(step.percent)}%
                  </span>

                  {step.completed ? (
                    <CheckCircle className="text-green-500" size={24} />
                  ) : step.active ? (
                    <LoaderCircle
                      className="text-purple-600 animate-spin"
                      size={24}
                    />
                  ) : (
                    <Hourglass className="text-slate-400" size={24} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-2xl shadow-sm">
          <h3 className="font-bold text-lg mb-5">Optimization Status</h3>

          <StatusRow  title="Image Uploaded" done={progress >= 5} />
          <StatusRow title="Image Analysis" done={progress >= 25} />
          <StatusRow title="Variations Created" done={progress >= 50} />
          <StatusRow title="Shipping Check" done={progress >= 75} />
          <StatusRow
            title="Finalizing Result"
            done={progress >= 100}
            active={progress >= 75 && progress < 100}
          />

          <div className="mt-6 bg-purple-50 rounded-2xl p-4 flex gap-3">
            <Lightbulb className="text-purple-600 shrink-0" />
            <p className="m-0 text-slate-700 text-sm">
              We are checking multiple image variations to find the lowest
              possible shipping charge.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatusRow({ title, done, active }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-5">
      <div className="flex items-center gap-3">
        {done ? (
          <CheckCircle className="text-green-500" size={18} />
        ) : active ? (
          <LoaderCircle className="text-purple-600 animate-spin" size={18} />
        ) : (
          <span className="w-[18px] h-[18px] rounded-full bg-slate-300 block" />
        )}

        <span className="font-medium text-xs text-slate-700">{title}</span>
      </div>

      <Tag color={done ? "green" : active ? "purple" : "default"}>
        {done ? "Completed" : active ? "In Progress" : "Pending"}
      </Tag>
    </div>
  );
}